import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { amountValidator, dateFormatValidator, fileValidator, getRequiredFieldsByNationality, minLessThanMaxValidator, noFutureDateValidator, percentageValidator } from '../../../../../shared/utils/functions';
import { InputField } from "../../../../../shared/ui/input-field/input-field";
import { DatePicker } from "../../../../../shared/components/date-picker/date-picker";
import { CustomDropdown } from "../../../../../shared/components/custom-dropdown/custom-dropdown";
import { countries } from '../../../../../shared/utils/data';
import { SingleFileUpload } from "../../../../../shared/components/single-file-upload/single-file-upload";
import { Button } from "../../../../../shared/ui/button/button";
import { KeyPersonnelFormKey } from '../../../../../shared/utils/types';
import { ARABIC_REGEX, ENGLISH_REGEX, KEY_PERSONNEL_DETAILS_FORM_KEYS } from '../../../../../shared/utils/constants';
import { RadioGroup } from "../../../../../shared/ui/radio-group/radio-group";

@Component({
  selector: 'app-common-form',
  imports: [InputField, TranslatePipe, CommonModule, DatePicker, CustomDropdown, ReactiveFormsModule, SingleFileUpload, Button, RadioGroup],
  templateUrl: './common-form.html',
  styleUrl: './common-form.css'
})
export class CommonForm {
  @Input() data: any;
  @Output() onFormCompletion = new EventEmitter<any>();
  @Input() activeForm!: KeyPersonnelFormKey;

  formIdentifier = KEY_PERSONNEL_DETAILS_FORM_KEYS;
  countriesOptions = countries;

  selectedNationality = '';
  arabicRequired: string[] = [];
  englishRequired: string[] = [];

  typeOfAuthorizationOptions = [
    { label: 'Financial' },
    { label: 'Managerial' },
    { label: 'Legal' }
  ];

  corporatePositionOptions = [
    { label: 'General Manager' },
    { label: 'Compliance Manager' },
    { label: 'Executive Manager' },
    { label: 'Financial Manager' }
  ];

  isCloseRelativeOptions = [
    { label: 'COMMON_FORM.YES', value: 'yes' },
    { label: 'COMMON_FORM.NO', value: 'no' }
  ];

  commonForm = new FormGroup(
    {
      nationality: new FormControl('', [Validators.required]),

      dob: new FormControl(null, [
        Validators.required,
        dateFormatValidator,
        noFutureDateValidator
      ]),

      // Arabic fields (required applied later based on nationality)
      firstNameAr: new FormControl('', [
        Validators.pattern(ARABIC_REGEX)
      ]),
      fatherNameAr: new FormControl('', [
        Validators.pattern(ARABIC_REGEX)
      ]),
      grandFatherNameAr: new FormControl('', [
        Validators.pattern(ARABIC_REGEX)
      ]),
      surnameAr: new FormControl('', [
        Validators.pattern(ARABIC_REGEX)
      ]),

      // English fields (required applied later based on nationality)
      firstNameEn: new FormControl('', [
        Validators.pattern(ENGLISH_REGEX)
      ]),
      fatherNameEn: new FormControl('', [
        Validators.pattern(ENGLISH_REGEX)
      ]),
      grandFatherNameEn: new FormControl('', [
        Validators.pattern(ENGLISH_REGEX)
      ]),
      surnameEn: new FormControl('', [
        Validators.pattern(ENGLISH_REGEX)
      ]),

      // Percentage fields
      individualShare: new FormControl('', [
        percentageValidator('individualShare')
      ]),
      corporateShare: new FormControl('', [
        percentageValidator('corporateShare')
      ]),

      // Conditional fields (required added based on form)
      position: new FormControl(''),
      typeOfAuthorization: new FormControl(''),

      // Amount fields
      minAuthAmount: new FormControl('', [
        amountValidator('minAuthAmount')
      ]),
      maxAuthAmount: new FormControl('', [
        amountValidator('maxAuthAmount')
      ]),

      // Always required
      isCloseRelative: new FormControl('', [Validators.required]),

      // Always required file fields
      nationalIdDocument: new FormControl('', [
        Validators.required,
        fileValidator(['jpg', 'png', 'pdf', 'doc'], 5)
      ]),

      proofOfAddressDocument: new FormControl('', [
        Validators.required,
        fileValidator(['jpg', 'png', 'pdf', 'doc'], 5)
      ])
    },
    {
      validators: [
        minLessThanMaxValidator('minAuthAmount', 'maxAuthAmount')
      ]
    }
  );

  constructor() { }

  ngOnInit(): void {
    this.applyActiveFormValidators();
  }

  applyActiveFormValidators() {
    const form = this.commonForm;

    // Clear conditional field validators first
    const conditionalFields = [
      'individualShare',
      'corporateShare',
      'position',
      'typeOfAuthorization',
      'minAuthAmount',
      'maxAuthAmount'
    ];

    conditionalFields.forEach(field => {
      form.get(field)?.clearValidators();
      form.get(field)?.updateValueAndValidity({ emitEvent: false });
    });

    // Apply validators based on active form
    switch (this.activeForm) {

      // INDIVIDUAL OWNERS — show individualShare
      case this.formIdentifier.INDIVIDUAL_OWNERS:
        form.get('individualShare')?.setValidators([
          Validators.required,
          percentageValidator('individualShare')
        ]);
        break;

      // EXECUTIVE MANAGEMENT — corporateShare + position required
      case this.formIdentifier.EXECUTIVE_MANAGEMENT:
        form.get('corporateShare')?.setValidators([
          Validators.required,
          percentageValidator('corporateShare')
        ]);
        form.get('position')?.setValidators([Validators.required]);
        break;

      // AUTHORIZED SIGNATORY — type + min/max required
      case this.formIdentifier.AUTHORIZED_SIGNATORY:
        form.get('typeOfAuthorization')?.setValidators([Validators.required]);

        form.get('minAuthAmount')?.setValidators([
          Validators.required,
          amountValidator('minAuthAmount')
        ]);

        form.get('maxAuthAmount')?.setValidators([
          Validators.required,
          amountValidator('maxAuthAmount')
        ]);
        break;
    }

    // Update validity after applying validators
    conditionalFields.forEach(field => {
      form.get(field)?.updateValueAndValidity({ emitEvent: false });
    });
  }


  onFileSelected(file: File, controlName: string) {
    this.commonForm.get(controlName)?.setValue(file);
  }

  onFileRemoved(controlName: string) {
    this.commonForm.get(controlName)?.reset();
  }

  onDropdownSelect(controlName: string, selectedItem: any) {
    this.commonForm.get(controlName)?.setValue(selectedItem.label);

    if (controlName === 'nationality') {
      this.onNationalityChange(selectedItem.code);
    }
  }

  getControl(controlName: string): FormControl {
    return this.commonForm.get(controlName) as FormControl;
  }

  onNationalityChange(code: string) {
    this.selectedNationality = code;

    const fields = getRequiredFieldsByNationality(code);

    this.arabicRequired = fields.arabicRequired;
    this.englishRequired = fields.englishRequired;

    this.updateValidators();
  }

  /** Update required validators for both language sections */
  updateValidators() {
    const allArabic = ['firstNameAr', 'fatherNameAr', 'grandFatherNameAr', 'surnameAr'];
    const allEnglish = ['firstNameEn', 'fatherNameEn', 'grandFatherNameEn', 'surnameEn'];

    // Clear validators
    [...allArabic, ...allEnglish].forEach(field => {
      this.commonForm.get(field)?.clearValidators();
      this.commonForm.get(field)?.updateValueAndValidity();
    });

    // Set Arabic required
    this.arabicRequired.forEach(key => {
      this.commonForm.get(key)?.setValidators([Validators.required, Validators.pattern(ARABIC_REGEX)]);
      this.commonForm.get(key)?.updateValueAndValidity();
    });

    // Set English required
    this.englishRequired.forEach(key => {
      this.commonForm.get(key)?.setValidators([Validators.required,  Validators.pattern(ENGLISH_REGEX)]);
      this.commonForm.get(key)?.updateValueAndValidity();
    });
  }

  isNationalitySelected(): boolean {
    return !!this.selectedNationality;
  }

  /** Handle radio button update */
  onPepSelection(value: string) {
    this.commonForm.get('isCloseRelative')?.setValue(value);
  }

  /** Final submit */
  handleFormSubmission() {
    if (this.commonForm.invalid) {
      this.commonForm.markAllAsTouched();
      return;
    }

    this.onFormCompletion.emit(this.commonForm.value);
  }


}
